import { CalendarClock, CircleDollarSign, FileCheck2, FileText, Landmark, Paperclip, PieChart, Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Kpi, Section } from '@/components/common/Blocks'
import { EmptyState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/lib/auth/AuthProvider'
import { fmtDate, fmtMoney } from '../../../shared/format'
import { downloadCrmFile, useCollections, useInvoices, useReceipts } from '../../api'
import { CRM_FINANCE_PERM } from '../../permissions'
import type { CustomerSummary } from '../../types'
import type { CrmDialog } from '../CrmDialogs'
import { toastError } from '../../ui'
import { CollectionsList } from '../RecordLists'
import { useCrmDeletes } from '../useCrmDeletes'

export function FinanceTab({ customerId, summary: s, open }: { customerId: string; summary: CustomerSummary; open: (d: CrmDialog) => void }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const collections = useCollections(customerId)
  const invoices = useInvoices(customerId)
  const receipts = useReceipts(customerId)
  const deletes = useCrmDeletes()
  const canPay = can(CRM_FINANCE_PERM)
  const cols = collections.data ?? []
  const invs = invoices.data ?? []
  const recs = receipts.data ?? []
  const codeOf = new Map(cols.map((c) => [c.id, c.code]))

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Kpi icon={FileCheck2} tone="primary" label={t('crm.summary.contract')} value={fmtMoney(s.contract)} />
        <Kpi icon={CircleDollarSign} tone="success" label={t('crm.summary.collected')} value={fmtMoney(s.collected)} footer={`%${s.collectedPct}`} />
        <Kpi icon={PieChart} tone="warning" label={t('crm.summary.remaining')} value={fmtMoney(s.remaining)} />
      </div>
      <Section
        title={t('crm.collection.title')}
        icon={CalendarClock}
        bodyClassName="p-0"
        actions={
          canPay ? (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => open({ kind: 'collection' })}>
                <Plus />
                {t('crm.collection.add')}
              </Button>
              <Button size="sm" onClick={() => open({ kind: 'receipt' })}>
                <Landmark />
                {t('crm.receipt.add')}
              </Button>
            </div>
          ) : null
        }
      >
        <CollectionsList rows={cols} canPay={canPay} onReceipt={(c) => open({ kind: 'receipt', collectionId: c.id })} />
      </Section>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Section
          title={t('crm.invoice.listTitle')}
          icon={FileText}
          bodyClassName="p-0"
          actions={
            canPay ? (
              <Button size="sm" variant="outline" onClick={() => open({ kind: 'invoice' })}>
                <Plus />
                {t('crm.invoice.add')}
              </Button>
            ) : null
          }
        >
          {invs.length === 0 ? (
            <EmptyState icon={FileText} title={t('crm.invoice.empty')} />
          ) : (
            <ul className="divide-y">
              {invs.map((i) => (
                <li key={i.id} className="flex items-center gap-3 px-4 py-3">
                  <FileText className="size-5 shrink-0 text-destructive" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-mono text-sm font-medium">{i.number}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {fmtDate(i.date)}
                      {i.collectionId ? ` · ${codeOf.get(i.collectionId) ?? ''}` : ''}
                      {i.fileName ? (
                        <>
                          {' · '}
                          <button type="button" className="hover:text-foreground hover:underline" onClick={() => void downloadCrmFile('invoice', i.id, i.fileName).catch(toastError)}>
                            {i.fileName}
                          </button>
                        </>
                      ) : null}
                    </p>
                  </div>
                  <span className="text-sm font-semibold tabular-nums">{fmtMoney(i.amount)}</span>
                  {canPay ? (
                    <Button size="icon" variant="ghost" className="size-8 hover:text-destructive" aria-label={t('common.delete')} onClick={() => void deletes.invoice(i)}>
                      <Trash2 />
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Section>
        <Section title={t('crm.receipt.listTitle')} icon={Landmark} bodyClassName="p-0">
          {recs.length === 0 ? (
            <EmptyState icon={Landmark} title={t('crm.receipt.empty')} />
          ) : (
            <ul className="divide-y">
              {recs.map((r) => (
                <li key={r.id} className="flex items-center gap-3 px-4 py-3">
                  <Paperclip className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{codeOf.get(r.collectionId) ?? '—'}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {fmtDate(r.date)} · {r.reference || '—'}
                      {r.fileName ? (
                        <>
                          {' · '}
                          <button type="button" className="hover:text-foreground hover:underline" onClick={() => void downloadCrmFile('receipt', r.id, r.fileName).catch(toastError)}>
                            {r.fileName}
                          </button>
                        </>
                      ) : null}
                    </p>
                  </div>
                  <span className="text-sm font-semibold tabular-nums text-success">+{fmtMoney(r.amount)}</span>
                  {canPay ? (
                    <Button size="icon" variant="ghost" className="size-8 hover:text-destructive" aria-label={t('common.delete')} onClick={() => void deletes.receipt(r)}>
                      <Trash2 />
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </div>
  )
}
